/* eslint-disable security/detect-object-injection */
import { ComponentType } from '@angular/cdk/portal';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { FilterOptions } from 'src/app/shared/models/filters/filter-options';
import { FilterDateModalComponent } from '../../molecules/filter-date-modal/filter-date-modal.component';
import { ChipContentOption } from 'src/app/shared/models/chip-content/chip-content-option';

export interface MultiSelectChip {
  value: string;
  isActive: boolean;
  filterType: string;
  id: any;
}

/**
 * Generate MultiSelectChip data for product series list
 * @param {any} productSeriesList
 * @return {MultiSelectChip[]} a list of MultiSelectChip
 */
export function generateSeriesChips(productSeriesList: any): MultiSelectChip[] {
  const seriesChips: MultiSelectChip[] = [];

  for (const productSeries of productSeriesList) {
    seriesChips.push({
      value: 'Series ' + productSeries.name,
      isActive: false,
      filterType: productSeries.name,
      id: productSeries.id,
    });
  }

  return seriesChips;
}

@Component({
  selector: 'app-filter-container',
  templateUrl: './filter-container.component.html',
  styleUrls: ['./filter-container.component.scss'],
})
/**
 * Filter Container
 */
export class FilterContainerComponent implements OnInit, OnChanges {
  @Output() selectFilterClick = new EventEmitter<ChipContent[] | any>();
  @Input() baseSimpleChipData: ChipContent[] = [];
  @Input() filters!: FilterOptions;
  @Input() statusChips: MultiSelectChip[] = [];
  @Input() seriesChips: MultiSelectChip[] = [];
  @Input() sortOptions!: ChipContentOption[];
  @Input() isSingleSelection: boolean = false;
  subregionChips!: MultiSelectChip[];
  orderAscOptions!: ChipContentOption[];
  orderLabel = 'a-z';
  sortLabel = 'most-reclaimed';

  /**
   * Constructor
   */
  constructor(private readonly dialog: MatDialog) {}

  /**
   * Initializer
   */
  ngOnInit(): void {
    // For business list, separated routine
    this.orderAscOptions = [
      {
        key: 'true',
        text: 'order-az',
        type: 'sort',
      },
      {
        key: 'false',
        text: 'order-za',
        type: 'sort',
      },
    ];
  }

  /**
   * ngOnChanges
   * @param {SimpleChanges} changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['seriesChips'] && this.seriesChips?.length > 0) {
      if (this.isSingleSelection) {
        this.initializeSingleSeries();
      }
    }
  }

  /**
   * Selects or unselects chip and update filter object
   * @param {ChipContent | MultiSelectChip} chip
   * @param { string } filterName
   * @param {any} event
   */
  onChipSelectionChange(
    chip: ChipContent | MultiSelectChip,
    filterName: string,
    event?: any
  ) {
    if (event) {
      event.stopPropagation();
    }
    if (chip instanceof ChipContent) {
      chip.isActive = !chip.isActive;
      if (chip.value === 'dateRange') {
        this.openDialog(FilterDateModalComponent).subscribe({
          next: (response) => {
            this.filters.dateRange = response as any;
          },
          error: () => {},
          complete: () => {
            this.selectFilterClick.emit(this.filters);
          },
        });
      } else if (!this.isSingleSelection) {
        this.selectFilterClick.emit(this.filters);
      } else {
        // **Single selection logic**

        // Reset all filters to false
        Object.keys(this.filters).forEach((key) => {
          (this.filters as Record<string, boolean>)[key] = false;
        });

        // Activate only the clicked filter
        (this.filters as Record<string, boolean>)[chip.value!] = true;

        // Update chip states
        this.baseSimpleChipData.forEach((c) => {
          c.isActive = c.filterType === chip.filterType;
        });
        this.selectFilterClick.emit(this.filters);
      }
    } else {
      if (this.isSingleSelection) {
        const chipList = this.getChipListByName(filterName);
        chipList.forEach((c) => (c.isActive = false));
        chip.isActive = true;
        (this.filters as any)[filterName] = [chip.id];
        this.selectFilterClick.emit(this.filters);
      } else {
        chip.isActive = !chip.isActive;
        this.updateFilterArrayField(filterName, chip);
        this.selectFilterClick.emit(this.filters);
      }
    }
  }

  /**
   *
   * @param {string} filterName
   * @param {MultiSelectChip} chip
   */
  private updateFilterArrayField(filterName: string, chip: MultiSelectChip) {
    switch (filterName) {
      case 'series': {
        const index = this.filters.series?.indexOf(chip.id);
        index === -1
          ? this.filters.series?.push(chip.id)
          : this.filters.series?.splice(index!, 1);
        break;
      }
      case 'subregion': {
        const index = this.filters.subregions?.indexOf(chip.id);
        index === -1
          ? this.filters.subregions?.push(chip.id)
          : this.filters.subregions?.splice(index!, 1);
        break;
      }
      case 'region': {
        const index = this.filters.regions?.indexOf(chip.id);
        index === -1
          ? this.filters.regions?.push(chip.id)
          : this.filters.regions?.splice(index!, 1);
        break;
      }
      default:
        break;
    }
  }

  // TODO refactor to accept other types of sorting
  /**
   *
   * @param {string} value
   */
  onSortByClick(value: string) {
    const sort = value;
    this.sortLabel = this.sortOptions.find((o) => o.key === sort)!.text;

    this.filters = { ...this.filters, sort: sort };
    this.selectFilterClick.emit(this.filters);
  }

  // TODO refactor to accept other types of sorting
  /**
   *
   * @param {string} value
   */
  onOrderByClick(value: string) {
    const order = value === 'true' ? true : false;
    this.orderLabel = order ? 'a-z' : 'z-a';

    this.filters = { ...this.filters, orderAsc: order };
    this.selectFilterClick.emit(this.filters);
  }

  /**
   * Unselects a filter
   * @param {ChipContent | MultiSelectChip} chip
   */
  unselectFilter(chip: ChipContent | MultiSelectChip) {
    chip.isActive = false;
    if (!(chip instanceof ChipContent)) {
      this.filters.regions?.splice(this.filters.regions.indexOf(chip.id), 1);
    }
    this.selectFilterClick.emit(this.filters);
  }

  /**
   * Unselects all chips and updates filter object
   */
  unselectAll() {
    this.baseSimpleChipData.forEach(
      (chip: ChipContent | MultiSelectChip) => {}
    );
    this.statusChips.forEach((c) => (c.isActive = false));
    this.seriesChips.forEach((c) => (c.isActive = false));
    if (Object.prototype.hasOwnProperty.call(this.filters, 'regions')) {
      this.filters.regions = [];
    }
    if (Object.prototype.hasOwnProperty.call(this.filters, 'series')) {
      this.filters.series = [];
    }
    this.selectFilterClick.emit(this.filters);
  }

  /**
   *
   * @return {Array}
   */
  getSelectedChips(): Array<any> {
    return [...this.baseSimpleChipData].filter((chip) => chip.isActive);
  }

  /**
   *
   * @return {Array<MultiSelectChip>}
   */
  getSelectedSubregionChips(): Array<MultiSelectChip> {
    return this.subregionChips.filter((chip) => chip.isActive);
  }

  /**
   *
   * @return {Array<MultiSelectChip>}
   */
  getSelectedStatusChips(): Array<MultiSelectChip> {
    return this.statusChips.filter((chip) => chip.isActive);
  }

  /**
   *
   * @return {Array<MultiSelectChip>}
   */
  getSelectedSeriesChips(): Array<MultiSelectChip> {
    return this.seriesChips.filter((chip) => chip.isActive);
  }

  /**
   * Instantiates a dialog and returns the user selected choice after it is closed
   * @param {ComponentType<T>} component
   * @return {Observable<MatDialogResponse>}
   */
  private openDialog<T>(
    component: ComponentType<T>
  ): Observable<MatDialogResponse> {
    const dialogRef = this.dialog.open(component, {
      autoFocus: false,
    });

    return dialogRef.afterClosed() as Observable<MatDialogResponse>;
  }

  /**
   * gets the correct styling for chip's text
   * @param {ChipContent} chip chipIcon name
   * @return {string} string - which class from the scss to use
   */
  getTextStyling(chip: ChipContent): string {
    const result = chip.icon !== undefined ? 'chip-text-icon' : 'chip-text';
    return result;
  }

  /**
   * return the correct multi select chip
   * @param {string} filterName chipIcon name
   * @return {MultiSelectChip[]} string - which class from the scss to use
   */
  getChipListByName(filterName: string): MultiSelectChip[] {
    switch (filterName) {
      case 'series':
        return this.seriesChips;
      case 'status':
        return this.statusChips;
      case 'subregion':
        return this.subregionChips;
      default:
        return [];
    }
  }

  /**
   * initialize single select series
   */
  initializeSingleSeries() {
    const hasSelection = this.seriesChips.some((c) => c.isActive);

    if (!hasSelection) {
      this.seriesChips[0].isActive = true;

      if (this.filters) {
        this.filters.series = [this.seriesChips[0].id];
      }

      this.selectFilterClick.emit(this.filters);
    }
  }
}
